import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, Search, X } from 'lucide-react';
import {
  TaskyAlert,
  TaskyBadge,
  TaskyButton,
  TaskyInput,
  TaskySelect,
  TaskySpinner,
  TaskyTable,
  TaskyTableBody,
  TaskyTableCell,
  TaskyTableHead,
  TaskyTableHeaderCell,
  TaskyTableRow,
} from '../tasky';
import adminPartnerPortalService, {
  PartnerReferral,
  PartnerReferralSource,
  PortalPartner,
} from '../../services/api/adminPartnerPortalService';

interface PartnerReferralListProps {
  /** 絞り込みプルダウン用のパートナー企業マスタ（親が取得済みのものを共有する）。 */
  partners: PortalPartner[];
}

const SOURCE_LABEL: Record<PartnerReferralSource, string> = {
  invite_url: '招待URL',
  portal_provision: 'ポータル発行',
};

const STATUS_LABEL: Record<string, string> = {
  active: '有料・契約中',
  trial: 'トライアル中',
  trial_expired: 'トライアル終了',
  suspended: '停止中',
  churned: '解約',
};

const STATUS_VARIANT: Record<string, 'success' | 'info' | 'warning' | 'secondary' | 'destructive'> = {
  active: 'success',
  trial: 'info',
  trial_expired: 'warning',
  suspended: 'secondary',
  churned: 'destructive',
};

const formatDate = (value: string | null): string => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' });
};

const extractErrorMessage = (error: any, fallback: string): string =>
  error?.response?.data?.error?.message || error?.message || fallback;

/**
 * パートナー経由で入ってきた組織の一覧。
 *
 * 招待URL経由（partner_ref）とポータルからの直接発行（external_ref）は
 * 帰属の記録先が別のため、経路を1列で見分けられるようにしている。
 */
const PartnerReferralList: React.FC<PartnerReferralListProps> = ({ partners }) => {
  const [referrals, setReferrals] = useState<PartnerReferral[]>([]);
  const [total, setTotal] = useState(0);
  const [directoryAvailable, setDirectoryAvailable] = useState(true);
  const [partnerFilter, setPartnerFilter] = useState('');
  const [keyword, setKeyword] = useState('');
  const [appliedKeyword, setAppliedKeyword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await adminPartnerPortalService.getReferrals({
        partner: partnerFilter || undefined,
        q: appliedKeyword || undefined,
      });
      setReferrals(result.organizations);
      setTotal(result.total);
      setDirectoryAvailable(result.portal_directory_available);
    } catch (err: any) {
      console.error('Failed to load partner referrals:', err);
      setError(extractErrorMessage(err, 'パートナー経由の組織一覧を取得できませんでした'));
    } finally {
      setIsLoading(false);
    }
  }, [partnerFilter, appliedKeyword]);

  useEffect(() => {
    load();
  }, [load]);

  // 絞り込みの選択肢は企業マスタを正としつつ、マスタから消えたコードの組織も
  // 一覧から辿れるよう、実データ側にしか無いコードを補って表示する。
  const partnerOptions = useMemo(() => {
    const options = partners.map(partner => ({ value: partner.code, label: `${partner.name}（${partner.code}）` }));
    const known = new Set(partners.map(partner => partner.code.toUpperCase()));
    referrals.forEach(referral => {
      const code = referral.partner_ref;
      if (code && !known.has(code.toUpperCase())) {
        known.add(code.toUpperCase());
        options.push({ value: code, label: code });
      }
    });
    return options;
  }, [partners, referrals]);

  const paidCount = referrals.filter(referral => referral.paid_active).length;
  const trialCount = referrals.filter(referral => referral.status === 'trial').length;

  return (
    <section>
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-base font-semibold text-dark">パートナー経由の組織</h2>
          <p className="mt-1 text-xs text-gray-500">
            招待URL経由とポータルからの直接発行の両方をまとめて表示します。
            <span className="font-medium text-dark">管理画面で手動で紐付けた組織も「招待URL」に含まれます</span>
            （記録が同じ列のため区別できません）。
          </p>
        </div>
        <TaskyButton size="sm" variant="secondary" onClick={load} disabled={isLoading}>
          <RefreshCw />
          再読み込み
        </TaskyButton>
      </div>

      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
        <TaskySelect
          value={partnerFilter}
          onChange={event => setPartnerFilter(event.target.value)}
          className="sm:w-72"
        >
          <option value="">すべてのパートナー</option>
          {partnerOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </TaskySelect>
        <form
          onSubmit={event => {
            event.preventDefault();
            setAppliedKeyword(keyword.trim());
          }}
          className="flex flex-1 items-center gap-2"
        >
          <TaskyInput
            value={keyword}
            onChange={event => setKeyword(event.target.value)}
            placeholder="組織名で絞り込み"
            className="sm:w-72"
          />
          <TaskyButton type="submit" size="sm" variant="secondary" disabled={isLoading}>
            <Search />
            絞り込み
          </TaskyButton>
          {appliedKeyword && (
            <TaskyButton
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setKeyword('');
                setAppliedKeyword('');
              }}
            >
              <X />
              解除
            </TaskyButton>
          )}
        </form>
      </div>

      {!directoryAvailable && (
        <TaskyAlert tone="warning" className="mb-4">
          パートナーポータルの企業マスタを取得できないため、企業名の代わりに紹介コードを表示しています。
        </TaskyAlert>
      )}

      {error && (
        <TaskyAlert tone="error" className="mb-4">
          {error}
        </TaskyAlert>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
        <span>
          該当 <span className="font-semibold text-dark">{total}</span> 組織
          {referrals.length < total && <>（表示は新しい順に{referrals.length}件）</>}
        </span>
        <span>
          有料 <span className="font-semibold text-dark">{paidCount}</span>
        </span>
        <span>
          トライアル中 <span className="font-semibold text-dark">{trialCount}</span>
        </span>
      </div>

      {isLoading && referrals.length === 0 ? (
        <div className="flex items-center justify-center gap-3 py-10 text-sm text-gray-500">
          <TaskySpinner size="md" />
          読み込み中...
        </div>
      ) : (
        <TaskyTable className="min-w-[1040px]">
          <TaskyTableHead>
            <TaskyTableRow hoverable={false}>
              <TaskyTableHeaderCell>組織名</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>パートナー企業</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>経路</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>登録日</TaskyTableHeaderCell>
              <TaskyTableHeaderCell className="whitespace-nowrap">トライアル終了</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>プラン</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>状態</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>代表アカウント</TaskyTableHeaderCell>
            </TaskyTableRow>
          </TaskyTableHead>
          <TaskyTableBody>
            {referrals.length === 0 ? (
              <TaskyTableRow hoverable={false}>
                <TaskyTableCell colSpan={8} className="py-8 text-center text-gray-500">
                  該当する組織がありません
                </TaskyTableCell>
              </TaskyTableRow>
            ) : (
              referrals.map(referral => (
                <TaskyTableRow key={referral.id}>
                  <TaskyTableCell className="font-medium text-dark">
                    {referral.name}
                    <span className="ml-2 text-xs text-gray-400">#{referral.id}</span>
                  </TaskyTableCell>
                  <TaskyTableCell>
                    {referral.partner_name ?? referral.partner_ref ?? '—'}
                    {referral.partner_status === 'suspended' && (
                      <TaskyBadge variant="secondary" className="ml-2">
                        停止中
                      </TaskyBadge>
                    )}
                    {referral.partner_name && referral.partner_ref && (
                      <div className="text-xs text-gray-400">{referral.partner_ref}</div>
                    )}
                  </TaskyTableCell>
                  <TaskyTableCell className="whitespace-nowrap">
                    <TaskyBadge variant={referral.source === 'portal_provision' ? 'warm' : 'outline'}>
                      {SOURCE_LABEL[referral.source]}
                    </TaskyBadge>
                  </TaskyTableCell>
                  <TaskyTableCell className="whitespace-nowrap">{formatDate(referral.created_at)}</TaskyTableCell>
                  <TaskyTableCell className="whitespace-nowrap">{formatDate(referral.trial_ends_at)}</TaskyTableCell>
                  <TaskyTableCell>{referral.plan ?? '—'}</TaskyTableCell>
                  <TaskyTableCell className="whitespace-nowrap">
                    <TaskyBadge variant={STATUS_VARIANT[referral.status] ?? 'secondary'}>
                      {STATUS_LABEL[referral.status] ?? referral.status}
                    </TaskyBadge>
                  </TaskyTableCell>
                  <TaskyTableCell>
                    <div className="text-sm text-dark">{referral.leader_name ?? '—'}</div>
                    <div className="text-xs text-gray-500">{referral.leader_email ?? '—'}</div>
                  </TaskyTableCell>
                </TaskyTableRow>
              ))
            )}
          </TaskyTableBody>
        </TaskyTable>
      )}
    </section>
  );
};

export default PartnerReferralList;
